/**
 * Skupni gradniki strani Kazalniki (Pregled, glava, Ustvarjanje vrednosti):
 * celica razčlenitve iz delov, zapis obdobja in sprememb ter ikona lupe.
 */
import type { DelFiltra, VrsticaPorocila } from '@koda/shared';
import type { JSX, KeyboardEvent } from 'react';

import { decimalno } from '../lib/format';
import type { Celica } from './PanelRazclenitve';

/**
 * Celica razclenitve iz delov (sestavin) z lastnimi filtri.
 *
 * Stevilke Pregleda niso vrstice tabele, zato si celica vrstico sestavi sama;
 * razclenitev bere samo dele - vsak del ima svoj filter in znesek.
 */
export function celicaDelov(naziv: string, naslovObdobja: string, vrednost: string, deli: DelFiltra[]): Celica {
  const prvi = deli[0]?.filter;
  const filter = prvi ?? { porocilo: 'kazalniki' as const, leto: 0, obdobjeOd: 0, obdobjeDo: 0 };
  const vrstica: VrsticaPorocila = {
    kljuc: naziv,
    naziv,
    sifra: null,
    opomba: null,
    zneski: [],
    skupaj: vrednost,
    filter,
    klikljiva: true,
    otroci: [],
  };
  return {
    vrstica,
    obdobjeOd: filter.obdobjeOd,
    obdobjeDo: filter.obdobjeDo,
    naslovObdobja,
    znesek: vrednost,
    deli: deli.map((d) => ({ naslov: d.naziv, filter: d.filter, znesek: d.znesek })),
  };
}

/**
 * Enter in preslednica sprožita klik: element grafa z `role="button"` in
 * `tabIndex` mora delovati tudi s tipkovnico, ne samo z miško.
 */
export function naTipko(akcija: () => void): (e: KeyboardEvent) => void {
  return (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      akcija();
    }
  };
}

/** Odstotek z izrecnim predznakom in eno decimalko (rast v okencu Gibanje). */
export function sPredznakom(v: string): string {
  return `${Number(v) > 0 ? '+' : ''}${decimalno(v, 1)}`;
}

/** Lupa: nevtralna ikona signala (brez rdeče in zelene). */
export function Lupa(): JSX.Element {
  return (
    <svg className="lupa" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="6.5" cy="6.5" r="4.5" />
      <line x1="10" y1="10" x2="14.5" y2="14.5" />
    </svg>
  );
}

/** Obdobje januar–mesec n z veliko začetnico, npr. »Januar–julij 2026«. */
export function obdobjeLeta(n: number, leto: number, meseci: string[]): string {
  const prvi = meseci[0] ?? '1';
  const zacetek = `${prvi.charAt(0).toUpperCase()}${prvi.slice(1)}`;
  return `${n === 1 ? zacetek : `${zacetek}–${meseci[n - 1] ?? String(n)}`} ${String(leto)}`;
}

/** Vsota zneskov delov v centih (brez napak plavajoče vejice), dve decimalki. */
export function vsotaDelov(deli: DelFiltra[]): string {
  const centi = deli.reduce((v, d) => v + Math.round(Number(d.znesek) * 100), 0);
  return (centi / 100).toFixed(2);
}
